import { reactive } from 'vue';

import type { LightboxImage } from './types';

interface LightboxState {
  open: boolean;
  items: LightboxImage[];
  index: number;
}

/**
 * Module-scoped singleton driving the one `UImageLightbox` mounted at the app
 * root (same pattern as `upload-registry`). Deliberately not provide/inject:
 * the primary caller is a Tiptap NodeView, which is rendered through a
 * teleport out of the normal component tree — a plain import always resolves,
 * whatever the editor does with its render context.
 */
const state = reactive<LightboxState>({
  open: false,
  items: [],
  index: 0,
});

export const lightboxState = state;

export const openLightbox = (items: LightboxImage[], index = 0): void => {
  if (items.length === 0) {
    return;
  }

  state.items = items;
  state.index = Math.min(Math.max(index, 0), items.length - 1);
  state.open = true;
};

export const closeLightbox = (): void => {
  state.open = false;
};

/** Wrap-around navigation — matches Linear's arrow-key behaviour. */
export const stepLightbox = (delta: number): void => {
  const total = state.items.length;

  if (total < 2) {
    return;
  }

  state.index = (state.index + delta + total) % total;
};

/** Marker attribute an image must carry to take part in the gallery. */
const LIGHTBOX_IMAGE_SELECTOR = 'img[data-lightbox]';

/**
 * The scope a gallery is collected from. `.tiptap` is the ProseMirror root, so
 * one description or one comment is one gallery; `[data-lightbox-root]` lets
 * non-editor surfaces opt in.
 */
const GALLERY_ROOT_SELECTOR = '.tiptap, [data-lightbox-root]';

/**
 * A rich-text `image` node carries whatever `src` its author wrote — the tiptap
 * payload is stored as an opaque JSON scalar, and the stock Image extension has
 * no protocol guard (unlike Link). A `javascript:` src is inert in `<img>`, but
 * the lightbox puts the same string on an `<a href>` it then clicks, which
 * would execute it. Only schemes that can name an image get through.
 */
const SAFE_URL_SCHEME = /^(?:https?:|blob:|data:image\/)/i;

const safeUrl = (value: string | null | undefined): string | null =>
  (value && SAFE_URL_SCHEME.test(value) ? value : null);

const toItem = (element: HTMLImageElement): LightboxImage => ({
  // `currentSrc` resolves srcset; falls back to `src` when there is none.
  src: safeUrl(element.currentSrc || element.src) ?? '',
  alt: element.alt || null,
  downloadUrl: safeUrl(element.dataset.lightboxDownload),
  fileName: element.dataset.lightboxName || null,
});

/**
 * Opens the lightbox on `element`, with every sibling image of the same
 * rich-text root as the surrounding gallery. Reading the DOM keeps the callers
 * dumb — a node view knows its own image, not the document it lives in — and
 * yields the images in exactly the order the reader sees them.
 */
export const openLightboxFromImage = (element: HTMLImageElement): void => {
  const root = element.closest(GALLERY_ROOT_SELECTOR);
  const images = root
    ? [...root.querySelectorAll<HTMLImageElement>(LIGHTBOX_IMAGE_SELECTOR)]
    : [element];

  // A root that somehow does not contain the clicked image still opens it alone.
  const index = images.indexOf(element);

  if (index === -1) {
    openLightbox([toItem(element)], 0);

    return;
  }

  openLightbox(images.map(toItem), index);
};
